import React, { useState, useRef, useEffect, useMemo } from "react";
import { POS_COLORS } from "../data/players";
import { STAT_LABELS } from "../data/scoring";
import { SPEED_SETTINGS } from "../data/archetypes";
import { getActual2025PPG } from "../engine/projections";
import { getAdpRank, getAdpValue, getEffectivePosRank, parseRankNum } from "../data/adpData";
import { loadOverrides, resolveDisplayTier } from "../data/displayTiers";
import { BYE_WEEKS_2026 } from "../data/scheduleData";
import theme from "../data/theme";
import { draftValueKey, draftValuesStorageKey } from "../utils/draftValuesBridge";
import { pprLabel } from "../utils/projlabProjection";
import { buildAutoDraftValues, AUTO_VALUE_SOURCE } from "../utils/autoDraftValues";
import { StarToggle, AvoidToggle, AvoidMark } from "./ListToggles";
import StatsButton, { StatsHintStrip } from "./StatsButton";
import PlayerAvatar from "./PlayerAvatar";

// One-time read of the explicit "Build Draft Values" snapshot from projlab, for
// the ACTIVE league (per-league key — building values in another league can't
// clobber this one). Returns the full snapshot { league, teams, budget,
// scoringLabel, values } or null if none exists (→ the My Value column is hidden
// entirely). Read once; the snapshot is stable mid-draft by design.
function readMyDraftValues(league) {
  try {
    return JSON.parse(localStorage.getItem(draftValuesStorageKey(league))) || null;
  } catch { return null; }
}

const PlayerList = React.memo(function PlayerList(props) {
  if (props.renderMode === "builder") return <BuilderPlayerList {...props} />;
  return <AuctionPlayerList {...props} />;
});

function BuilderPlayerList({
  players, search, onSearchChange, posFilter, onToggleFilter, isFilterActive,
  sortBy, onSortChange,
  getPlayerValue, getPlayerProj, getPlayerGames, getPlayerVorp, scoring,
  customStats, getPlayerStats,
  getAvailableSlots, maxBid, emptySlots,
  onAddToRoster,
  expandedPlayer, setExpandedPlayer,
  positions, mobileTab,
  scrollToAffordable,
  watchlist, onToggleWatchlist,
  avoids, onToggleAvoid,
  activeLeagueName,
}) {
  // The user's Tier Builder drags for THIS league, for the tier badge. Read on
  // mount and on league change, exactly as TierViewer does (TierViewer.jsx:83)
  // — this list unmounts when you switch to the Tiers tab, so coming back is a
  // fresh mount and the badge is fresh with it.
  const displayMoves = useMemo(() => loadOverrides(activeLeagueName), [activeLeagueName]);
  const scrollRef = useRef(null);
  useEffect(() => {
    if (!scrollToAffordable || !scrollRef.current) return;
    // Delay to let the filtered list re-render
    const t = setTimeout(() => {
      const container = scrollRef.current;
      if (!container) return;
      const rows = container.querySelectorAll('.player-row');
      for (const row of rows) {
        const op = parseFloat(row.style.opacity);
        if (op > 0.5) {
          row.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          return;
        }
      }
      // Fallback: scroll to top
      container.scrollTop = 0;
    }, 50);
    return () => clearTimeout(t);
  }, [scrollToAffordable]);
  return (
    <div className={`panel${mobileTab !== "players" ? " player-panel-hide" : ""}`} style={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1, overflow: "hidden" }}>
      {/* Filters */}
      <div className="player-filter-bar">
        <input value={search} onChange={e => onSearchChange(e.target.value)} placeholder="Search players..."
          style={{ background: theme.colors.bg.surface, border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, padding: "7px 12px", color: theme.colors.text.primary, fontSize: 13, width: 180, fontFamily: "inherit", minWidth: 0, flex: "1 1 140px" }}
        />
        <div className="pos-filters" style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {positions.map(pos => {
            const active = isFilterActive(pos);
            const bgColor = pos === "ALL" ? theme.colors.brand.orange : POS_COLORS[pos]?.bg || theme.colors.brand.orange;
            return (
            <button key={pos} className="btn" onClick={() => onToggleFilter(pos)} style={{
              background: active ? bgColor : theme.colors.bg.elevated,
              color: active ? "#fff" : theme.colors.text.muted,
              padding: "5px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700, letterSpacing: "0.5px", fontFamily: "inherit",
              border: active ? "none" : "1px solid transparent",
            }}>{pos}</button>
            );
          })}
        </div>
        <div className="sort-buttons" style={{ marginLeft: "auto", display: "flex", gap: 4, alignItems: "center" }}>
          <span className="sort-label" style={{ fontSize: 11, color: theme.colors.text.secondary, marginRight: 4 }}>Sort:</span>
          {[["rank", "Rank"], ["value", "Price"], ["ppg", "PPG"], ["tier", "Tier"], ["adp", "ADP"], ["vorp", "VORP", true], ["proj", "Pts", true], ["name", "A-Z", true]].map(([key, label, desktopOnly]) => (
            <button key={key} className={`btn${desktopOnly ? " sort-desktop" : ""}`} onClick={() => onSortChange(key)} style={{
              background: sortBy === key ? theme.colors.border.default : "transparent",
              color: sortBy === key ? theme.colors.text.primary : theme.colors.text.faint,
              padding: "4px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600, fontFamily: "inherit",
            }}>{label}</button>
          ))}
        </div>
      </div>

      <StatsHintStrip />

      {/* Column Headers */}
      <div className="player-grid-header">
        <span>POS</span><span>PLAYER</span><span className="team-col" style={{ textAlign: "center" }}>TEAM</span>
        <span style={{ textAlign: "right" }}>PRICE</span><span className="adp-col" style={{ textAlign: "right" }}>ADP</span><span className="proj-col-header" style={{ textAlign: "right" }}>PROJ</span><span className="ppg-col ppg-actual" style={{ textAlign: "right" }}>PPG</span><span className="ppg-col ppg-proj" style={{ textAlign: "right" }}><span className="ppg-label-desktop">pPPG</span><span className="ppg-label-mobile">PPG</span></span><span className="vorp-col" style={{ textAlign: "right" }}>VORP</span><span className="bye-col" style={{ textAlign: "right" }}>BYE</span>
        <span className="tier-col" style={{ textAlign: "right" }}>TIER</span>
      </div>

      {/* Player List */}
      <div ref={scrollRef} className="player-list-scroll" style={{ flex: 1, overflowY: "auto", minHeight: 200 }}>
        {players.map((player, i) => {
          const posColor = POS_COLORS[player.pos];
          const hasSlot = getAvailableSlots(player).length > 0;
          const val = getPlayerValue(player);
          const overBudget = val > maxBid && emptySlots > 0;
          const available = hasSlot && !overBudget;
          const isCustomProj = customStats[`${player.name}-${player.pos}`] !== undefined;
          const proj = getPlayerProj(player);
          const vorp = getPlayerVorp(player);
          const playerKey = `${player.name}-${player.pos}`;
          const isExpanded = expandedPlayer === playerKey;
          return (
            <div key={`${player.name}-${player.pos}-${i}`}>
              <div className="player-row player-grid-row"
                onClick={() => setExpandedPlayer(prev => prev === playerKey ? null : playerKey)}
                title={overBudget ? `Over budget — max bid is $${Math.max(0, maxBid)}` : !hasSlot ? "No roster slot available" : ""}
                style={{
                  borderBottom: isExpanded ? "none" : "1px solid rgba(255,255,255,0.02)",
                  opacity: available ? 1 : 0.3,
                  background: isExpanded ? theme.helpers.brandBg(0.06) : overBudget ? "rgba(239, 68, 68, 0.04)" : "transparent",
                }}>
                <span className="tag" style={{ background: posColor.bg, color: posColor.text, fontSize: 10, width: 36 }}>{getEffectivePosRank(player.name, player.posRank) || player.pos}</span>
                <span className="player-name-col" style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0, overflow: "hidden" }}>
                  <StarToggle name={player.name} watchlist={watchlist} onToggleWatchlist={onToggleWatchlist} size={13} />
                  <AvoidToggle name={player.name} avoids={avoids} onToggleAvoid={onToggleAvoid} size={12} />
                  {/* Hidden at <=820px: two 44px touch targets + the avatar
                      would leave the name ~78px at 360px. The avatar is
                      decorative, the name is not. Desktop keeps it. */}
                  <span className="player-row-avatar" style={{ display: "flex", flexShrink: 0 }}>
                    <PlayerAvatar player={player} size={22} />
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", opacity: avoids?.has(player.name) ? 0.55 : 1 }}>
                    <AvoidMark name={player.name} avoids={avoids} />{player.name}{player.team && <span className="inline-team" style={{ marginLeft: 4, fontSize: 10, fontWeight: 600, color: theme.colors.text.muted }}>{player.team}</span>}
                  </span>
                  {player.pos !== "DEF" && player.pos !== "K" && (
                    <StatsButton name={player.name} source="builder" />
                  )}
                  {overBudget && <span style={{ fontSize: 10, color: theme.colors.status.danger, fontWeight: 700, letterSpacing: "0.5px", flexShrink: 0 }}>OVER BUDGET</span>}
                </span>
                <span className="team-col" style={{ textAlign: "center", fontSize: 11, color: theme.colors.text.secondary, fontWeight: 600 }}>{player.team}</span>
                <div style={{ textAlign: "right" }}>
                  <span className="val-col" style={{ fontSize: 13, fontWeight: 700, color: theme.colors.brand.accent }}>${val}</span>
                </div>
                <span className="adp-col" style={{ textAlign: "right", fontSize: 11, color: theme.colors.text.secondary, fontWeight: 500 }}>{getAdpValue(player.name) || "\u2014"}</span>
                <span className="proj-col-header" style={{ textAlign: "right", fontSize: 12, color: isCustomProj ? theme.colors.status.success : theme.colors.text.secondary }}>{proj}</span>
                {/* 🔴 His OWN games, not 17: the projections are normalised to a
                    partial season (~15 games) to price in games missed, so a
                    /17 rate read ~12% low on every skill player — and right on
                    DEF/K, whose line is extrapolated to 17 before scoring. That
                    mismatch is what let a K out-project a TE in this column. */}
                {(() => { const actualPPG = getActual2025PPG(player.name, player.pos, scoring); const projPPG = Math.round((proj / (getPlayerGames ? getPlayerGames(player) : 17)) * 10) / 10; return (<>
                  <span className="ppg-col ppg-actual" style={{ textAlign: "right", fontSize: 11, color: theme.colors.text.secondary }}>{actualPPG != null ? actualPPG : "\u2014"}</span>
                  <span className="ppg-col ppg-proj" style={{ textAlign: "right", fontSize: 11, color: theme.colors.text.secondary }}>{projPPG}</span>
                </>); })()}
                <span className="vorp-col" style={{ textAlign: "right", fontSize: 12, fontWeight: 700, color: vorp >= 30 ? theme.colors.status.success : vorp >= 15 ? theme.colors.text.primary : vorp >= 5 ? theme.colors.status.warning : theme.colors.status.danger }}>{vorp}</span>
                <span className="bye-col" style={{ textAlign: "right", fontSize: 11, color: theme.colors.text.secondary }}>{BYE_WEEKS_2026[player.team] ?? "—"}</span>
                <span className="tier-col" style={{ textAlign: "right", fontSize: 10, fontWeight: 700 }}>
                  {(() => {
                    // THE USER'S OWN TIER, not the price engine's.
                    //
                    // This read used to be `userTierOverrides[POS:Name]` — the
                    // dead price key. Nothing has written it since the Jul 21
                    // display-tier rewrite and index.js purges it before render
                    // (tier-diagnostic-aug9.md §HEADLINE), so the badge was
                    // structurally always the canonical TIER_MAPS band and
                    // silently ignored every drag the user made in Tier Builder.
                    //
                    // It now resolves through the SAME function the Tier Builder
                    // board and TierViewer group by, off the live
                    // userDisplayTierMoves_<league> key. Display only — no price
                    // path reads this, and the severance gate
                    // (tools/severanceGate.test.js) pins that.
                    const rn = parseRankNum(getEffectivePosRank(player.name, player.posRank) || player.posRank);
                    const t = resolveDisplayTier(player.pos, player.name, rn, displayMoves);
                    if (t == null) return "";
                    const tierColor = t <= 2 ? "#22c55e" : t <= 4 ? "#eab308" : t <= 6 ? "#FF9500" : "#64748b";
                    const tierBg = t <= 2 ? "rgba(34,197,94,0.15)" : t <= 4 ? "rgba(234,179,8,0.15)" : t <= 6 ? "rgba(255,149,0,0.15)" : "rgba(100,116,139,0.15)";
                    return <span style={{ background: tierBg, color: tierColor, padding: "1px 5px", borderRadius: 4, fontSize: 9, letterSpacing: "0.3px" }}>T{t}</span>;
                  })()}
                </span>
              </div>
              {isExpanded && (
                <div className="player-expanded-row">
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 14px", marginBottom: 8 }}>
                    {Object.entries(getPlayerStats(player)).map(([statKey, sVal]) => (
                      <div key={statKey} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <span style={{ fontSize: 10, color: theme.colors.text.secondary, fontWeight: 600 }}>{STAT_LABELS[statKey] || statKey}</span>
                        <span style={{ fontSize: 11, fontWeight: 600, color: theme.colors.text.primary }}>
                          {statKey === "ptsAllowedPerGame" || statKey.endsWith("BonusGames") ? Number(sVal).toFixed(1) : sVal}
                        </span>
                      </div>
                    ))}
                  </div>
                  {available && (
                    <button className="btn" onClick={e => { e.stopPropagation(); onAddToRoster(player); }}
                      style={{ background: theme.colors.brand.gradientBtn, color: "#fff", padding: "6px 14px", borderRadius: 6, fontSize: 11, fontWeight: 700, fontFamily: "inherit" }}>
                      Add to Roster
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="player-list-footer">
        <span>{players.length} players available</span>
        <span>Click proj to edit • Click player to add</span>
      </div>
    </div>
  );
}

function AuctionPlayerList({
  players, search, onSearchChange, posFilter, onToggleFilter,
  positions,
  watchlist, onToggleWatchlist,
  avoids, onToggleAvoid,
  canNominate, isUserTurn, mockAutopick,
  nominatingPlayer, onSetNominatingPlayer,
  openingBid, onOpeningBidChange, userMaxBid, onNominatePlayer,
  nomTimer, nomTimerMax, mockSpeed,
  mockMobileTab,
  nomGlow,
  tutorialHint,
  mobPanelTab,
  activeLeagueName,
  settings,
  dynamicPrices,
}) {
  const showNomHint = tutorialHint === "nominate-player";
  // Mobile only: the search field collapses to a magnifier in the chip row to
  // buy back its ~44px. Desktop never sees the toggle (display:none by default)
  // and never hides the input, so this state is inert there.
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef(null);
  // Pick the touch-verb by input type, not viewport (pointer type is stable
  // mid-session, so evaluate once per render — no listener needed).
  const isTouch = window.matchMedia?.("(pointer: coarse)")?.matches;
  // Personal draft values for the active league. A user-built Lab snapshot wins
  // (frozen mid-draft by design); otherwise build Mock1 Value from the live
  // field under the current league settings, so the column is never empty.
  // Deps are the settings FIELDS, not the object, so a parent re-render can't
  // rebuild every frame.
  const mySnap = useMemo(
    () => readMyDraftValues(activeLeagueName) || buildAutoDraftValues({ settings, league: activeLeagueName }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeLeagueName, settings?.teams, settings?.budget, JSON.stringify(settings?.positions), JSON.stringify(settings?.scoring)]
  );
  const isAutoValue = mySnap?.source === AUTO_VALUE_SOURCE;
  const myValues = mySnap?.values || null;
  // Snapshot is stale if it was built under different teams/budget/scoring than
  // the current draft. Coarse scoring signal only (projection hash is deferred).
  const valuesStale = !!mySnap && !isAutoValue && (
    mySnap.teams !== settings?.teams ||
    mySnap.budget !== settings?.budget ||
    mySnap.scoringLabel !== pprLabel(settings?.scoring)
  );
  // Width lives in styles/auction.css (.mock-players-panel), NOT inline: the
  // middle layout tier shrinks it to 280px via media query, and an inline width
  // would win over any rule that isn't !important. flexShrink stays inline — it
  // is constant across all three tiers.
  return (
    <div className={`mock-panel mock-players-panel${mockMobileTab !== "players" ? " mock-players-hide" : ""}${mobPanelTab === "tiers" ? " mob-tiers-active" : ""}${nomGlow ? " nom-glow-pulse" : ""}`} style={{ flexShrink: 0, display: "flex", flexDirection: "column" }}>
      <div className={`mock-panel-inner${showNomHint ? " tutorial-pulse" : ""}`} style={{ background: theme.colors.bg.surface, border: `1px solid ${theme.colors.border.subtle}`, borderRadius: 12, flex: 1, display: "flex", flexDirection: "column" }}>
        <div className="mock-search-row" style={{ padding: "10px 12px", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
          {/* onFocus, not tabIndex: the zero-box collapse keeps the input in the
              tab order while invisible, so tabbing to it expands it rather than
              parking focus on nothing. tabIndex={searchOpen ? 0 : -1} would look
              equivalent but searchOpen is viewport-blind and stays false on
              desktop — where the magnifier is display:none and can never be
              clicked — so it would drop the always-visible desktop input out of
              the tab order. Inert on desktop (already visible, and
              .mock-search-open does nothing outside the mobile block) and
              idempotent with the magnifier's own focus(). */}
          <input ref={searchInputRef} className={`mock-search-input${searchOpen ? " mock-search-open" : ""}`} value={search} onChange={e => onSearchChange(e.target.value)} onFocus={() => setSearchOpen(true)} placeholder="Search players..."
            style={{ width: "100%", background: "rgba(255,255,255,0.04)", border: `1px solid ${theme.colors.border.subtle}`,
              borderRadius: 6, padding: "7px 10px", color: theme.colors.text.primary, fontSize: 12, fontFamily: "inherit", marginBottom: 6 }} />
          <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
            {/* Closing clears the query on purpose: a collapsed field holding a
                live filter would hide players with no visible cause.
                focus() runs inside the handler, not an effect — the collapsed
                input is still rendered and focusable (zero box, not hidden),
                so iOS Safari sees the keyboard request inside the gesture. */}
            <button type="button" className="mock-search-toggle"
              onClick={() => {
                if (searchOpen) { setSearchOpen(false); onSearchChange(""); }
                else { searchInputRef.current?.focus(); setSearchOpen(true); }
              }}
              aria-label="Search players" aria-expanded={searchOpen} title="Search players"
              style={{
                padding: "3px 8px", borderRadius: 4, fontSize: 10, fontWeight: 700, border: "none", cursor: "pointer", fontFamily: "inherit",
                background: searchOpen ? theme.colors.brand.orange : "rgba(255,255,255,0.04)",
                color: searchOpen ? "#fff" : theme.colors.text.muted,
              }}>{"🔍"}</button>
            {positions.map(pos => {
              const isWatchBtn = pos === "★";
              const active = isWatchBtn
                ? posFilter.has("★")
                : (pos === "ALL" ? posFilter.has("ALL") : posFilter.has(pos) && !posFilter.has("ALL") && !posFilter.has("★"));
              const activeBg = isWatchBtn ? "#eab308" : (POS_COLORS[pos]?.bg || theme.colors.brand.orange);
              return (
                <button key={pos} onClick={() => onToggleFilter(pos)} style={{
                  padding: "3px 8px", borderRadius: 4, fontSize: 10, fontWeight: 700, border: "none", cursor: "pointer", fontFamily: "inherit",
                  background: active ? activeBg : "rgba(255,255,255,0.04)",
                  color: active ? (isWatchBtn ? "#000" : "#fff") : theme.colors.text.muted,
                }}>{pos}</button>
              );
            })}
          </div>
        </div>
        <StatsHintStrip />
        {isAutoValue && (
          <div style={{
            padding: "6px 12px", fontSize: 10.5, lineHeight: 1.35,
            color: theme.colors.text.faint, background: "transparent",
          }}>
            Mock1 Value = our projections priced for this league · build your own in Projection Lab
          </div>
        )}
        {valuesStale && (
          <div style={{
            padding: "6px 12px", fontSize: 10.5, lineHeight: 1.35,
            color: theme.colors.status.warning, background: "rgba(234,179,8,0.08)",
            borderBottom: "1px solid rgba(234,179,8,0.2)",
          }}>
            My Values may be stale — rebuild in Projection Lab.
          </div>
        )}
        <div className="mock-player-list" style={{ flex: 1, overflowY: "auto" }}>
          {players.slice(0, 80).map((player, idx) => {
            const posColor = POS_COLORS[player.pos];
            const canNom = isUserTurn && canNominate && !mockAutopick;
            // Canonical displayed price (composite key — bare-name lookups miss).
            const estPrice = dynamicPrices?.get(`${player.name}-${player.pos}`) ?? 1;
            return (
              <div key={player.name} className={`mock-pl-row${showNomHint && idx < 6 ? " tutorial-shimmer" : ""}`} onClick={() => canNom && onSetNominatingPlayer(player)}
                style={{
                  display: "flex", alignItems: "center", gap: 8, padding: "7px 12px",
                  borderBottom: "1px solid rgba(255,255,255,0.03)",
                  cursor: canNom ? "pointer" : "default",
                  background: nominatingPlayer?.name === player.name ? theme.helpers.brandBg(0.15) : canNom ? theme.helpers.brandBg(0.02) : "transparent",
                  transition: "background 0.15s ease",
                  ...(showNomHint && idx < 6 ? { animationDelay: `${idx * 0.5}s` } : {}),
                }}
                onMouseEnter={e => canNom && (e.currentTarget.style.background = nominatingPlayer?.name === player.name ? theme.helpers.brandBg(0.15) : theme.helpers.brandBg(0.08))}
                onMouseLeave={e => canNom && (e.currentTarget.style.background = nominatingPlayer?.name === player.name ? theme.helpers.brandBg(0.15) : theme.helpers.brandBg(0.02))}>
                {/* LEFT: two-line block \u2014 same rhythm as the two-line My Value cell,
                    so the name keeps room instead of ellipsizing to 2-4 chars */}
                <div className="mock-pl-stack" style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                  {/* gap 6 (not 4) keeps the \ud83d\udeab clear of the position pill at
                      360px, where the toggles are 44px touch targets */}
                  <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                <StarToggle name={player.name} watchlist={watchlist} onToggleWatchlist={onToggleWatchlist} />
                <AvoidToggle name={player.name} avoids={avoids} onToggleAvoid={onToggleAvoid} />
                <span className="tag" style={{ background: posColor.bg, color: posColor.text, fontSize: 9, padding: "1px 6px", minWidth: 30, flexShrink: 0 }}>{player.pos}</span>
                <PlayerAvatar player={player} size={24} />
                <span className="mock-pl-name" style={{ flex: 1, minWidth: 96, fontSize: 12, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", opacity: avoids?.has(player.name) ? 0.55 : 1 }}>
                  <AvoidMark name={player.name} avoids={avoids} />{player.name}
                </span>
                {player.pos !== "DEF" && player.pos !== "K" && (
                  <StatsButton name={player.name} source="mock_list" />
                )}
                  </div>
                  {/* Line 2 on desktop: muted micro info, indented under the
                      name (star+avoid+chip+avatar+gaps ≈ 105px). On mobile the
                      stack flips to a row and this sits beside the name. */}
                  <div className="mock-pl-meta" style={{ paddingLeft: 105, fontSize: 10, fontWeight: 500, color: theme.colors.text.muted, lineHeight: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {[
                      player.team,
                      getEffectivePosRank(player.name, player.posRank),
                      getAdpValue(player.name) || null,
                      `Bye ${BYE_WEEKS_2026[player.team] ?? "—"}`,
                    ].filter(Boolean).join(" · ")}
                  </div>
                </div>
                <span style={{ fontSize: 12, fontWeight: 700, color: theme.colors.brand.accent, flexShrink: 0 }}>${estPrice}</span>
                {myValues && (() => {
                  // My Value = personal projection-derived price (suffix-stripped
                  // key); market = the price shown in this very row (estPrice),
                  // so the delta reconciles with what the user sees.
                  // Display-only — never feeds the engine, nominations, or tiers.
                  const mv = myValues[draftValueKey(player.name, player.pos)];
                  if (mv == null) {
                    // Missing (K/DEF, unprojected) → em dash, never 0 or $1.
                    return <span className="mock-pl-myval" style={{ width: 46, flexShrink: 0, textAlign: "right", fontSize: 11, color: theme.colors.text.faint }} title={isAutoValue ? "No Mock1 value for this player (K/DEF are $1)" : "No personal value for this player"}>—</span>;
                  }
                  const market = estPrice;
                  const delta = mv - market;
                  const dColor = delta > 2 ? theme.colors.status.success : delta < -2 ? theme.colors.status.danger : theme.colors.text.muted;
                  const dStr = delta > 0 ? `+${delta}` : `${delta}`;
                  return (
                    <span className="mock-pl-myval" style={{ width: 46, flexShrink: 0, textAlign: "right", lineHeight: 1.15 }} title={`${isAutoValue ? "Mock1 value" : "My value"} $${mv} · market $${market} · ${delta >= 0 ? "+" : ""}${delta} vs market`}>
                      <span style={{ display: "block", fontSize: 11, fontWeight: 700, color: theme.colors.text.primary }}>${mv}</span>
                      <span style={{ display: "block", fontSize: 9, fontWeight: 700, color: dColor }}>{dStr}</span>
                    </span>
                  );
                })()}
              </div>
            );
          })}
        </div>
        {/* Nomination Bar */}
        {isUserTurn && !mockAutopick && nominatingPlayer && (
          <div className="mock-nomination-bar" style={{
            padding: "10px 12px", borderTop: "2px solid rgba(255,107,0,0.4)",
            background: `linear-gradient(180deg, ${theme.helpers.brandBg(0.12)} 0%, ${theme.colors.bg.surface} 100%)`,
            flexShrink: 0,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
              <span className="tag" style={{
                background: POS_COLORS[nominatingPlayer.pos]?.bg, color: POS_COLORS[nominatingPlayer.pos]?.text,
                fontSize: 9, padding: "1px 6px",
              }}>{nominatingPlayer.pos}</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: theme.colors.text.primary, fontFamily: theme.fonts.display }}>{nominatingPlayer.name}</span>
              <span style={{ fontSize: 11, color: theme.colors.text.secondary }}>{nominatingPlayer.team}</span>
              <span style={{ fontSize: 11, color: theme.colors.text.secondary }}>{nominatingPlayer.posRank}</span>
              <span style={{ marginLeft: "auto", fontSize: 11, color: theme.colors.brand.accent, fontWeight: 700 }}>${dynamicPrices?.get(`${nominatingPlayer.name}-${nominatingPlayer.pos}`) ?? 1} Est</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 11, color: theme.colors.text.secondary, fontWeight: 600, whiteSpace: "nowrap" }}>Open:</span>
              <span style={{ fontSize: 14, fontWeight: 800, color: theme.colors.brand.orange }}>$</span>
              <input type="number" value={openingBid}
                onChange={e => onOpeningBidChange(e.target.value)}
                min="1" max={userMaxBid}
                style={{
                  width: 56, background: theme.colors.bg.deep, border: `2px solid ${theme.colors.border.brand}`, borderRadius: 6,
                  padding: "6px 8px", color: theme.colors.brand.orange, fontSize: 14, fontWeight: 800,
                  fontFamily: "inherit", textAlign: "center",
                }}
                onKeyDown={e => {
                  if (e.key === "Enter") {
                    const bid = parseInt(openingBid) || 1;
                    onNominatePlayer(nominatingPlayer, bid);
                  }
                }}
              />
              {[5, 10, 15, 20].map(v => (
                <button key={v} onClick={() => onOpeningBidChange(String(v))} style={{
                  padding: "4px 0", width: 32, borderRadius: 5, fontSize: 11, fontWeight: 700,
                  fontFamily: "inherit", border: "1px solid rgba(255,255,255,0.08)", cursor: "pointer",
                  background: parseInt(openingBid) === v ? theme.colors.brand.orange : theme.colors.bg.raised,
                  color: parseInt(openingBid) === v ? "#000" : theme.colors.text.secondary,
                }}>${v}</button>
              ))}
              <button className="btn" onClick={() => {
                const bid = parseInt(openingBid) || 1;
                onNominatePlayer(nominatingPlayer, bid);
              }} style={{
                flex: 1, padding: "8px 12px", borderRadius: 8, fontSize: 13, fontWeight: 900,
                fontFamily: theme.fonts.display, letterSpacing: "0.5px",
                background: theme.colors.brand.gradientBtn, color: "#000",
                border: "none", cursor: "pointer",
              }}>NOMINATE</button>
              <span onClick={() => onSetNominatingPlayer(null)} style={{
                fontSize: 11, color: theme.colors.text.secondary, cursor: "pointer", fontWeight: 500, whiteSpace: "nowrap",
              }}>&times;</span>
            </div>
          </div>
        )}
        {isUserTurn && !mockAutopick && !nominatingPlayer && (
          <div className="mock-turn-strip" style={{
            padding: "12px 12px", borderTop: "2px solid rgba(255,107,0,0.4)",
            background: `linear-gradient(180deg, ${theme.helpers.brandBg(0.12)} 0%, ${theme.helpers.brandBg(0.04)} 100%)`,
            textAlign: "center", animation: "mockBidPulse 2s ease-in-out infinite",
          }}>
            <span style={{ fontSize: 13, fontWeight: 900, color: theme.colors.brand.orange, letterSpacing: "1.5px", fontFamily: theme.fonts.display }}>
              {"\u{1F3AF}"} YOUR TURN — {isTouch ? "TAP" : "CLICK"} A PLAYER{nomTimer !== null ? ` (${nomTimer}s)` : ""}
            </span>
            {nomTimer !== null && (
              <div style={{ marginTop: 6, height: 3, borderRadius: 2, background: theme.helpers.brandBg(0.15), overflow: "hidden" }}>
                <div className="mock-timer-bar" style={{
                  width: `${((nomTimer) / (nomTimerMax || SPEED_SETTINGS[mockSpeed]?.userNomSec || 30)) * 100}%`,
                  background: nomTimer > 10 ? theme.colors.brand.orange : nomTimer > 5 ? theme.colors.brand.accent : theme.colors.status.danger,
                }} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default PlayerList;
